import { useEffect, useState } from 'react'
import { ReactiveVar } from '@apollo/client'

/**
 * Drop-in replacement for Apollo's `useReactiveVar`, for components that also
 * call `useSubscription`.
 *
 * `useReactiveVar` is built on `useSyncExternalStore`. On React 18.2 a
 * render-phase state update anywhere in the same component leaves that store's
 * committed snapshot stale, and React then compares every later notification
 * against the stale snapshot — so setting a var to a new value and back stops
 * re-rendering entirely. `useSubscription` performs exactly such an update
 * whenever it recreates its observable (changed variables, `skip`, document or
 * fetch policy). Plain state has no snapshot bookkeeping, so it is unaffected.
 */
export const useReactiveVarSafe = <T>(rv: ReactiveVar<T>): T => {
  const [value, setValue] = useState(rv)

  useEffect(() => {
    let stopped = false

    const listen = (next: T) => {
      if (stopped) return

      setValue(() => next)
      // Listeners registered via onNextChange fire once, so re-arm every time.
      rv.onNextChange(listen)
    }

    const unsubscribe = rv.onNextChange(listen)
    // The var may already have moved on between render and this effect.
    setValue(() => rv())

    return () => {
      stopped = true
      unsubscribe()
    }
  }, [rv])

  return value
}
